import { useId } from "react";
import { ToggleSwitch } from "../shared/ToggleSwitch";
import adminStyles from "./Admin.module.css";
import panel from "./AdminPanel.module.css";
import styles from "./SettingPanel.module.css";

/**
 * A panel holding one setting, or a few that belong together: its title (with anything that acts on the whole setting, such as a switch,
 * at the right of it), what it does, then its fields. The settings tabs under System are built from these.
 */
export function SettingPanel({
  id,
  title,
  intro,
  headerActions,
  children,
}: {
  id: string;
  title: string;
  intro?: string;
  headerActions?: React.ReactNode;
  children?: React.ReactNode;
}): React.JSX.Element {
  return (
    <section className={`${panel.panel} ${styles.settingPanel}`} aria-labelledby={`${id}-heading`}>
      <div className={panel.header}>
        <h2 id={`${id}-heading`} className={`panel-title ${panel.title}`}>
          {title}
        </h2>
        {headerActions}
      </div>
      {intro !== undefined && <p className={panel.intro}>{intro}</p>}
      {children}
    </section>
  );
}

/** A switch for the whole setting, for a panel's header: what it controls, then the switch. */
export function SettingSwitch({ label, checked, onChange, disabled = false }: { label: string; checked: boolean; onChange: (checked: boolean) => void; disabled?: boolean }): React.JSX.Element {
  const labelId = useId();

  return (
    <span className={styles.headerSwitch}>
      <span id={labelId}>{label}</span>
      <ToggleSwitch checked={checked} onChange={onChange} labelledBy={labelId} disabled={disabled} />
    </span>
  );
}

/** Setting panels side by side where they fit, each row as tall as its tallest. */
export function SettingGrid({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <div className={styles.grid}>{children}</div>;
}

/** One of a panel's settings, recessed under its own subheading, with what it does beneath. */
export function SettingSubsection({ id, title, explanation, children }: { id: string; title: string; explanation: string; children?: React.ReactNode }): React.JSX.Element {
  return (
    <section className={styles.subsection} aria-labelledby={`${id}-heading`}>
      <h3 id={`${id}-heading`} className={panel.subheading}>
        {title}
      </h3>
      <p>{explanation}</p>
      {children}
    </section>
  );
}

/** A panel of several related settings, each a subsection, side by side where they fit. */
export function SettingGroup({ id, title, intro, children }: { id: string; title: string; intro?: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <SettingPanel id={id} title={title} intro={intro}>
      <div className={styles.subsections}>{children}</div>
    </SettingPanel>
  );
}

/** The form row a setting's fields sit in, the save button level with the boxes. */
export function SettingForm({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <div className={styles.form}>{children}</div>;
}

/** Why a setting's value cannot be saved yet, beneath its fields, in the warning colour. Its id is what the fields point at. */
export function SettingProblem({ id, children }: { id: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <p id={id} className={styles.problem} role="status">
      {children}
    </p>
  );
}

/** What a field reports about its value: whether it is invalid, and the element that says why. */
interface Validity {
  invalid?: boolean;
  describedBy?: string;
}

/** A number setting: its label above, a box as wide as the label (and at least wide enough for a number) below. */
export function NumberField({
  id,
  label,
  value,
  onChange,
  min,
  max,
  invalid = false,
  describedBy,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  min?: number;
  max?: number;
} & Validity): React.JSX.Element {
  return (
    <div className={`${adminStyles.field} ${styles.number}`}>
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type="number"
        inputMode="numeric"
        step="1"
        min={min}
        max={max}
        value={value}
        aria-invalid={invalid}
        aria-describedby={invalid ? describedBy : undefined}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

/** A text setting: its label above, a box that takes the row's spare width below. */
export function TextField({
  id,
  label,
  value,
  onChange,
  type = "text",
  placeholder,
  invalid = false,
  describedBy,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: "text" | "url";
  placeholder?: string;
} & Validity): React.JSX.Element {
  return (
    <div className={`${adminStyles.field} ${styles.text}`}>
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type={type}
        value={value}
        placeholder={placeholder}
        spellCheck={false}
        aria-invalid={invalid}
        aria-describedby={invalid ? describedBy : undefined}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}
